import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLegalPages } from './legal/legalPages';
import { appSignInUrl, appSignUpUrl } from './config/authUrls.js';
import { injectContactTokens } from './config/contact.js';
import { IS_COMING_SOON, SITE_MODE } from './config/siteMode';
import { MARKETING_VERSION } from './config/marketingVersion.js';
import {
  LANDING_VARIANT_V1,
  LANDING_VARIANT_V2,
  LANDING_VARIANT_V3,
  resolveFullModeLandingVariant,
} from './config/marketingSurface.js';
import { SeoHead } from './seo/SeoHead';
import { LottieLoader } from './components/loading/LottieLoader';
import { SiteRedirectInterstitial } from './components/interstitial/SiteRedirectInterstitial';

const IS_COMING_SOON_BUILD = SITE_MODE === 'coming-soon';
export function resolveHomepageVariantForApp({
  siteMode = SITE_MODE,
  marketingVersion = MARKETING_VERSION,
} = {}) {
  return resolveFullModeLandingVariant({ siteMode, marketingVersion });
}

const HOMEPAGE_VARIANT = resolveHomepageVariantForApp();
const LandingPageV1 = HOMEPAGE_VARIANT === LANDING_VARIANT_V1
  ? lazy(() => import('./pages/LandingPage'))
  : null;
const LandingPageV2 = HOMEPAGE_VARIANT === LANDING_VARIANT_V2
  ? lazy(() => import('./pages/LandingPageV2'))
  : null;
const LandingPageV3 = HOMEPAGE_VARIANT === LANDING_VARIANT_V3
  ? lazy(() => import('./pages/LandingPageV3'))
  : null;
const ComingSoonPage = lazy(() => import('./pages/ComingSoonPage'));
const WaitlistConfirmPage = lazy(() => import('./pages/WaitlistConfirmPage'));
const LegalPage = lazy(() =>
  import('./legal/LegalPage').then((mod) => ({ default: mod.LegalPage })),
);
const GanttParityDebugPage = IS_COMING_SOON_BUILD
  ? null
  : lazy(() =>
      import('./debug/gantt-parity/GanttParityDebugPage.jsx').then((mod) => ({ default: mod.default })),
    );

const BOOLEAN_TRUE_VALUES = new Set(['1', 'true', 'yes', 'on']);
const PARITY_DEBUG_ENABLED =
  import.meta.env.DEV &&
  BOOLEAN_TRUE_VALUES.has(String(import.meta.env.VITE_DEBUG_PARITY || '').trim().toLowerCase());

function RouteFallback() {
  const { t } = useTranslation();

  return (
    <LottieLoader label={t('legal.loading')} fullscreen />
  );
}

function LegalRoute({ page, title, locale }) {
  const document = useMemo(() => {
    if (page.kind !== 'structured') {
      return null;
    }

    return page.getDocument(locale);
  }, [locale, page]);

  const [markdown, setMarkdown] = useState(() => (page.kind === 'markdown' ? '' : null));

  useEffect(() => {
    if (page.kind !== 'markdown') {
      setMarkdown(null);
      return undefined;
    }

    let cancelled = false;
    page
      .loadContent(locale)
      .then((content) => {
        if (!cancelled) {
          setMarkdown(injectContactTokens(content));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMarkdown('');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [locale, page]);

  return (
    <LegalPage
      title={title}
      markdown={markdown || ''}
      document={document}
      tocMode={page.tocMode}
    />
  );
}

function ExternalRedirect({ href }) {
  const { t } = useTranslation();
  const [showSlowMessage, setShowSlowMessage] = useState(false);
  const [showManualContinue, setShowManualContinue] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (window.location.href !== href) {
      window.location.assign(href);
    }
  }, [href]);

  useEffect(() => {
    setShowSlowMessage(false);
    setShowManualContinue(false);

    const slowTimer = window.setTimeout(() => {
      setShowSlowMessage(true);
    }, 6000);
    const manualTimer = window.setTimeout(() => {
      setShowManualContinue(true);
    }, 11000);

    return () => {
      window.clearTimeout(slowTimer);
      window.clearTimeout(manualTimer);
    };
  }, [href]);

  return (
    <SiteRedirectInterstitial
      title={t('legal.redirecting')}
      why={t('legal.redirectingWhy', {
        defaultValue: 'cedrum is opening your secure destination in a new route.',
      })}
      statusMessage={t('legal.redirecting')}
      slowMessage={t('legal.redirectingSlow', {
        defaultValue: 'Still working... If redirect takes too long, continue manually.',
      })}
      showSlowMessage={showSlowMessage}
      showManualContinue={showManualContinue}
      continueHref={href}
      continueLabel={t('legal.continue')}
    />
  );
}

export default function App() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const legalPages = getLegalPages({ isComingSoon: IS_COMING_SOON });
  const locale = i18n.resolvedLanguage || i18n.language || 'en';
  const normalizedPathname =
    location.pathname && location.pathname.length > 1 && location.pathname.endsWith('/')
      ? location.pathname.slice(0, -1)
      : location.pathname || '/';
  const shouldNoIndex = normalizedPathname === '/confirm';

  return (
    <>
      <SeoHead noIndex={shouldNoIndex} />
      <Routes>
        {IS_COMING_SOON ? (
          <Route
            path="/"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ComingSoonPage />
              </Suspense>
            }
          />
        ) : (
          <>
            {LandingPageV1 ? (
              <Route
                path="/"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <LandingPageV1 />
                  </Suspense>
                }
              />
            ) : null}
            {LandingPageV2 ? (
              <Route
                path="/"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <LandingPageV2 />
                  </Suspense>
                }
              />
            ) : null}
            {LandingPageV3 ? (
              <Route
                path="/"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <LandingPageV3 />
                  </Suspense>
                }
              />
            ) : null}
          </>
        )}
        <Route
          path="/confirm"
          element={
            <Suspense fallback={<RouteFallback />}>
              <WaitlistConfirmPage />
            </Suspense>
          }
        />
        {legalPages.map((page) => (
          <Route
            key={page.path}
            path={page.path}
            element={
              <Suspense fallback={<RouteFallback />}>
                <LegalRoute
                  page={page}
                  title={t(page.titleKey)}
                  locale={locale}
                />
              </Suspense>
            }
          />
        ))}

        {!IS_COMING_SOON && import.meta.env.DEV ? (
          <Route
            path="/__sentry-test"
            element={<SentryTestPage />}
          />
        ) : null}

        {!IS_COMING_SOON && PARITY_DEBUG_ENABLED && GanttParityDebugPage ? (
          <Route
            path="/_debug/gantt-parity"
            element={(
              <Suspense fallback={<RouteFallback />}>
                <GanttParityDebugPage />
              </Suspense>
            )}
          />
        ) : null}

        {!IS_COMING_SOON ? <Route path="/sign-in/*" element={<ExternalRedirect href={appSignInUrl} />} /> : null}
        {!IS_COMING_SOON ? <Route path="/sign-up/*" element={<ExternalRedirect href={appSignUpUrl} />} /> : null}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function SentryTestPage() {
  throw new Error('Sentry test');
}
